import React, { useState, useRef, useMemo, useEffect } from 'react';
import {
  View,
  PanResponder,
  TouchableWithoutFeedback,
  Animated,
  Text,
  Dimensions,
  LayoutChangeEvent,
} from 'react-native';
import Svg, { Path, Polygon, Circle } from 'react-native-svg';
import Icon from '../../AppBar/Icon';
import styles from './style';

interface Node {
  id: number;
  name: string;
  connections: number[];
  iconName?: string;
  amount?: number; // Added amount property
  [key: string]: any;
}

interface SocialNetworkGraphProps {
  nodes?: Node[];
  nodeSize?: number;
  highlightedNodeSize?: number;
  nodeColor?: string;
  textColor?: string;
  highlightedNodeColor?: string;
  linkColor?: string;
  backgroundColor?: string;
  width?: number | string;
  height?: number | string;
  onNodePress?: (nodeId: number) => void;
  showLabels?: boolean;
  showAmounts?: boolean; // New prop to control amount display
  directional?: boolean;
  showArrows?: boolean;
  arrowSize?: number;
  curveIntensity?: number;
  iconName?: string;
  circleColors?: string[];
  minNodeSize?: number; // Minimum node size
  maxNodeSize?: number; // Maximum node size
  amountScaleFactor?: number; // Factor to scale amount value to node size
  amountTextColor?: string; // Color for amount text
  isZeroAmounts?: boolean;
}

const SocialNetworkGraph: React.FC<SocialNetworkGraphProps> = ({
  nodes = [],
  nodeSize = 20,
  highlightedNodeSize = 30,
  nodeColor = '#3498db',
  highlightedNodeColor = '#e74c3c',
  linkColor = '#95a5a6',
  textColor = 'white',
  width = 300,
  height = 300,
  onNodePress = () => {},
  showLabels = true,
  showAmounts = false, // Default to not showing amounts
  directional = false,
  showArrows = true,
  arrowSize = 8,
  curveIntensity = 0,
  iconName = null,
  circleColors = [],
  backgroundColor = 'transparent',
  isZeroAmounts = false,
  minNodeSize = 10,
  maxNodeSize = 40,
  amountScaleFactor = 0.01,
  amountTextColor = '#ffffff',
}) => {
  const [highlightedNode, setHighlightedNode] = useState<number | null>(null);
  const [containerSize, setContainerSize] = useState({
    width: 300,
    height: 300,
  });
  const containerRef = useRef<View>(null);

  // Handle container size changes
  useEffect(() => {
    if (typeof width === 'number' && typeof height === 'number') {
      setContainerSize({ width, height });
    }
  }, [width, height]);

  const onLayout = (event: LayoutChangeEvent) => {
    const { width: layoutWidth, height: layoutHeight } =
      event.nativeEvent.layout;
    setContainerSize({ width: layoutWidth, height: layoutHeight });
  };

  // Calculate the maximum amount value for scaling
  const maxAmount = useMemo(() => {
    if (nodes.length === 0) return 1;
    return Math.max(...nodes.map((node) => node.amount || 0), 1); // Use 1 as minimum to avoid division by zero
  }, [nodes]);

  // Convert nodes array to links
  const links = useMemo(() => {
    const linkMap = new Map<
      string,
      { source: number; target: number; bidirectional: boolean }
    >();
    const result: { source: number; target: number; bidirectional: boolean }[] =
      [];

    // First, collect all connections
    nodes.forEach((node) => {
      node.connections.forEach((connectionId) => {
        // Create a unique ID for each connection
        const minId = Math.min(node.id, connectionId);
        const maxId = Math.max(node.id, connectionId);
        const key = `${minId}-${maxId}`;

        if (directional) {
          // For directional graphs, add each connection separately
          result.push({
            source: node.id,
            target: connectionId,
            bidirectional: false, // Will update this in second pass
          });
        } else {
          // For bidirectional graphs, avoid duplicate links
          if (!linkMap.has(key)) {
            linkMap.set(key, {
              source: node.id,
              target: connectionId,
              bidirectional: false,
            });
          }
        }
      });
    });

    // For directional graphs, look for mutual connections
    if (directional) {
      // Create a map of existing connections for quick lookup
      const connectionExists = new Map<string, boolean>();
      result.forEach((link) => {
        connectionExists.set(`${link.source}-${link.target}`, true);
      });

      // Mark bidirectional connections
      result.forEach((link) => {
        const reverseKey = `${link.target}-${link.source}`;
        if (connectionExists.has(reverseKey)) {
          link.bidirectional = true;
        }
      });
    } else {
      // Convert map to array for bidirectional graphs
      linkMap.forEach((link) => {
        result.push(link);
      });
    }

    return result;
  }, [nodes, directional]);

  // Initialize positions in a circular layout
  const [positions, setPositions] = useState<
    Record<
      number,
      {
        x: number;
        y: number;
        animated: Animated.ValueXY;
      }
    >
  >({});

  useEffect(() => {
    const initialPositions: Record<
      number,
      {
        x: number;
        y: number;
        animated: Animated.ValueXY;
      }
    > = {};

    if (nodes.length === 0) {
      setPositions(initialPositions);
      return;
    }

    const centerX = containerSize.width / 2;
    const centerY = containerSize.height / 2;
    const radius = Math.min(containerSize.width, containerSize.height) * 0.4;
    const angleStep = (2 * Math.PI) / nodes.length;

    nodes.forEach((node, index) => {
      const angle = index * angleStep;
      initialPositions[node.id] = {
        x: centerX + Math.cos(angle) * radius - centerX,
        y: centerY + Math.sin(angle) * radius - centerY,
        animated: new Animated.ValueXY({ x: 0, y: 0 }),
      };
    });

    setPositions(initialPositions);
  }, [nodes, containerSize]);

  // Update pan responders when nodes change
  const panResponders = useMemo(() => {
    return nodes.reduce((acc, node) => {
      acc[node.id] = PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onPanResponderMove: (evt, gestureState) => {
          const { dx, dy } = gestureState;
          if (positions[node.id]?.animated) {
            positions[node.id].animated.setValue({
              x: dx,
              y: dy,
            });
          }
        },
        onPanResponderRelease: (evt, gestureState) => {
          const { dx, dy } = gestureState;
          if (!positions[node.id]) return;

          const newX = positions[node.id].x + dx;
          const newY = positions[node.id].y + dy;

          setPositions((prev) => ({
            ...prev,
            [node.id]: {
              ...prev[node.id],
              x: newX,
              y: newY,
              animated: new Animated.ValueXY({ x: 0, y: 0 }),
            },
          }));

          // Snap to center if released near center
          if (Math.abs(newX) < 20 && Math.abs(newY) < 20) {
            Animated.spring(positions[node.id].animated, {
              toValue: { x: -newX, y: -newY },
              useNativeDriver: true,
            }).start(() => {
              setPositions((prev) => ({
                ...prev,
                [node.id]: {
                  ...prev[node.id],
                  x: 0,
                  y: 0,
                  animated: new Animated.ValueXY({ x: 0, y: 0 }),
                },
              }));
            });
          }
        },
      });
      return acc;
    }, {} as Record<number, any>);
  }, [nodes, positions]);

  const handleNodePress = (nodeId: number) => {
    setHighlightedNode(nodeId === highlightedNode ? null : nodeId);
    onNodePress(nodeId);
  };

  // Function to calculate node size based on amount
  const calculateNodeSize = (node: Node, isHighlighted: boolean): number => {
    // If node has no amount, use default size
    if (!node.amount && node.amount !== 0) {
      return isHighlighted ? highlightedNodeSize : nodeSize;
    }

    // Calculate size based on amount
    const baseSize = isHighlighted ? highlightedNodeSize : nodeSize;

    // For zero amount, use the min size or base size, whichever is smaller
    if (isZeroAmounts) {
      return nodeSize;
    }
    if (node.amount === 0) {
      return Math.min(baseSize, minNodeSize);
    }

    // Scale the amount relative to the maximum amount
    const scale = node.amount / maxAmount;

    // Calculate size in range [minNodeSize, maxNodeSize]
    const calculatedSize = minNodeSize + scale * (maxNodeSize - minNodeSize);
    // If highlighted, add some extra size
    return isHighlighted ? calculatedSize + baseSize : calculatedSize;
  };

  // Function to generate a curved path between two points
  const generateCurvedPath = (
    sourceX: number,
    sourceY: number,
    targetX: number,
    targetY: number,
    bidirectional: boolean,
    sourceNodeSize: number,
    targetNodeSize: number
  ) => {
    // Calculate the direction vector
    const dx = targetX - sourceX;
    const dy = targetY - sourceY;

    // Calculate the angle of the line
    const angle = Math.atan2(dy, dx);

    // Calculate the distance between centers
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Use actual node sizes instead of average
    const sourceRadius = sourceNodeSize / 2;
    const targetRadius = targetNodeSize / 2;

    // Adjust start and end points to be at the edge of the nodes
    const startX = sourceX + sourceRadius * Math.cos(angle);
    const startY = sourceY + sourceRadius * Math.sin(angle);

    const endX = targetX - targetRadius * Math.cos(angle);
    const endY = targetY - targetRadius * Math.sin(angle);

    // For bidirectional links, curve more intensely
    const curveFactor = bidirectional ? curveIntensity * 1.5 : curveIntensity;

    // Calculate control point for the curve
    // Perpendicular to the line, at a distance proportional to the line length
    const perpX = -dy / distance;
    const perpY = dx / distance;

    // Different curve direction for each side of bidirectional connection
    const curveDirection = bidirectional && sourceX > targetX ? -1 : 1;

    const controlX =
      (startX + endX) / 2 + perpX * distance * curveFactor * curveDirection;
    const controlY =
      (startY + endY) / 2 + perpY * distance * curveFactor * curveDirection;

    // Calculate points for the arrow head
    const arrowTipX = endX;
    const arrowTipY = endY;

    // Calculate the angle from control point to arrow tip
    // This gives better alignment for the arrow on a curve
    const arrowAngle = Math.atan2(arrowTipY - controlY, arrowTipX - controlX);

    // Create arrow head points
    const arrowPoint1X =
      arrowTipX - arrowSize * Math.cos(arrowAngle - Math.PI / 7);
    const arrowPoint1Y =
      arrowTipY - arrowSize * Math.sin(arrowAngle - Math.PI / 7);
    const arrowPoint2X =
      arrowTipX - arrowSize * Math.cos(arrowAngle + Math.PI / 7);
    const arrowPoint2Y =
      arrowTipY - arrowSize * Math.sin(arrowAngle + Math.PI / 7);

    // Create SVG path
    const path = `M ${startX} ${startY} Q ${controlX} ${controlY}, ${endX} ${endY}`;

    return {
      path,
      arrowHead:
        showArrows && directional
          ? `${arrowTipX},${arrowTipY} ${arrowPoint1X},${arrowPoint1Y} ${arrowPoint2X},${arrowPoint2Y}`
          : null,
    };
  };

  // Function to get node color based on index or highlight
  const getNodeColor = (nodeId: number, index: number) => {
    if (highlightedNode === nodeId) {
      return highlightedNodeColor;
    }

    if (circleColors && circleColors.length > 0) {
      return circleColors[index % circleColors.length];
    }

    return nodeColor;
  };

  // Format amount for display
  const formatAmount = (amount: number | undefined): string => {
    if (amount === undefined) return '';

    // Format with comma separators
    return amount.toLocaleString();
  };

  return (
    <View
      ref={containerRef}
      style={[
        styles.container,
        {
          width: typeof width === 'string' ? width : width + 'px',
          height: typeof height === 'string' ? height : height + 'px',
          backgroundColor: backgroundColor,
          padding: 20,
          paddingBottom: 50,
        },
      ]}
      onLayout={onLayout}
    >
      <View
        style={[
          styles.graphContainer,
          { width: containerSize.width, height: containerSize.height },
        ]}
      >
        <Svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${containerSize.width} ${containerSize.height}`}
        >
          {links.map((link, index) => {
            const sourcePos = positions[link.source];
            const targetPos = positions[link.target];

            if (!sourcePos || !targetPos) return null;

            // Find source and target nodes
            const sourceNode = nodes.find((n) => n.id === link.source);
            const targetNode = nodes.find((n) => n.id === link.target);

            if (!sourceNode || !targetNode) return null;

            // Get actual positions including center offset
            const sourceX = containerSize.width / 2 + sourcePos.x;
            const sourceY = containerSize.height / 2 + sourcePos.y;
            const targetX = containerSize.width / 2 + targetPos.x;
            const targetY = containerSize.height / 2 + targetPos.y;

            // Determine node sizes (based on amounts if available)
            const isSourceHighlighted = highlightedNode === link.source;
            const isTargetHighlighted = highlightedNode === link.target;

            const sourceSize = calculateNodeSize(
              sourceNode,
              isSourceHighlighted
            );
            const targetSize = calculateNodeSize(
              targetNode,
              isTargetHighlighted
            );

            // Get path and arrow information
            const { path, arrowHead } = generateCurvedPath(
              sourceX,
              sourceY,
              targetX,
              targetY,
              link.bidirectional,
              sourceSize,
              targetSize
            );

            return (
              <React.Fragment key={`connection-${index}`}>
                {/* Draw the curved path */}
                <Path d={path} stroke={linkColor} strokeWidth={1} fill="none" />

                {/* Draw arrow head if needed */}
                {arrowHead && <Polygon points={arrowHead} fill={linkColor} />}
              </React.Fragment>
            );
          })}
        </Svg>

        {/* Render nodes */}
        {nodes.map((node, index) => {
          const isHighlighted = highlightedNode === node.id;
          const size = calculateNodeSize(node, isHighlighted);
          const color = getNodeColor(node.id, index);
          const zIndex = isHighlighted ? 10 : 1;
          const pos = positions[node.id];
          const nodeIconName = node.iconName || iconName;

          if (!pos) return null;

          return (
            <Animated.View
              key={node.id}
              style={[
                styles.node,
                {
                  width: size,
                  height: size,
                  borderRadius: size / 2,
                  backgroundColor: nodeIconName ? 'transparent' : color,
                  zIndex,
                  left: containerSize.width / 2 + pos.x - size / 2,
                  top: containerSize.height / 2 + pos.y - size / 2,
                  transform: [
                    { translateX: pos.animated.x },
                    { translateY: pos.animated.y },
                  ],
                },
              ]}
              {...panResponders[node.id]?.panHandlers}
            >
              <TouchableWithoutFeedback
                onPress={() => handleNodePress(node.id)}
              >
                <View style={styles.touchableArea}>
                  {nodeIconName ? (
                    <Icon name={nodeIconName} size={size} color={color} />
                  ) : null}
                  {showAmounts && node.amount !== undefined && (
                    <View style={{ width: '100', display: 'flex' }}>
                      {node.amount > 0 && (
                        <Text
                          style={{
                            width: '100%',
                            fontSize: size * 0.4,
                            opacity: 0.8,
                            color: textColor,
                            fontWeight: 'bold',
                            textAlign: 'center',
                            backgroundColor: color,
                            borderRadius: 100,
                            paddingHorizontal: 5,
                          }}
                        >
                          {formatAmount(node.amount)}
                        </Text>
                      )}
                    </View>
                  )}
                  {showLabels && (
                    <Text
                      style={[
                        styles.label,
                        {
                          fontSize: size * 0.4,
                          top:
                            size +
                            (showAmounts && nodeIconName && node['amount']
                              ? size * 0.4
                              : 5),
                          width: size * 2.5,
                          left: -size + size / 4,
                          color: textColor,
                        },
                      ]}
                    >
                      {node.name}
                    </Text>
                  )}
                </View>
              </TouchableWithoutFeedback>
            </Animated.View>
          );
        })}
      </View>
    </View>
  );
};

export default SocialNetworkGraph;
